import { AppIntegrationsRepository, MeRepository } from 'common/repositories/index';
import ObjectComparer from 'common/services/util/-RFC.ObjectComparer';
import DeviceNotifications from 'common/notifications/-RFC.deviceNotifications';
import Util from 'common/services/util/-RFC.util.js';

let _devices = [];
const observableDevices = new Rx.BehaviorSubject(_devices);

/**
 * @ngdoc service
 * @name repositories.DevicesRepository
 *
 * @description
 * This class is used to save/add devices objects.
 */
const DevicesRepository = {

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#replace
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function can be used to replace all stored devices with provided data.
     *
     * @param {Array} data An array with new devices.
     */
    replace(data) {

        _devices = (data || []).filter((device) => {

            if (String(Util.deepFind(device, 'relationships.user.data.id')) ===
                String(MeRepository.getMyId())) {

                return device;

            }

            return false;

        });
        observableDevices.onNext(_devices);

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#add
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function can be used to add a new device to repository.
     *
     * @param {object} device An device object.
     */
    add(device) {

        if (String(Util.deepFind(device, 'relationships.user.data.id')) ===
            String(MeRepository.getMyId())) {

            const indexOfDevice = _devices
                .findIndex(_device => String(_device.id) === String(device.id));

            if (indexOfDevice === -1) {

                DeviceNotifications.processCreate(device);

                _devices.push(device);
                observableDevices.onNext(_devices);

            }

        }

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#remove
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function can be used to remove a device from repository.
     *
     * @param {integer} deviceId Id of device to be removed.
     */
    remove(deviceId) {

        const indexOfDevice = _devices
            .findIndex(_device => String(_device.id) === String(deviceId));

        if (indexOfDevice >= 0) {

            _devices.splice(indexOfDevice, 1);
            observableDevices.onNext(_devices);

        }

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#update
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function can be used to update a device from repository.
     *
     * @param {object} device a device object with new values.
     */
    update(device) {

        if (String(Util.deepFind(device, 'relationships.user.data.id')) ===
            String(MeRepository.getMyId())) {

            const indexOfDevice = _devices
                .findIndex(_device => String(_device.id) === String(device.id));

            if (indexOfDevice >= 0) {

                const flattenDiff = ObjectComparer.getFlattenDiff(_devices[indexOfDevice], device);
                DeviceNotifications.processUpdate(flattenDiff, _devices[indexOfDevice], device);

                if (!angular.equals(flattenDiff, {})) {

                    _devices[indexOfDevice] = device;
                    observableDevices.onNext(_devices);

                }

            }

        }

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#isDeviceCompatibleWithIntegration
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function checks if an integration can be installed on a specified device.
     *
     * @param {int} deviceId id of device
     * @param {string} appIntegrationId id of integration
     *
     * @returns {boolean} True if the device is compatible with the integration; false otherwise
     */
    isDeviceCompatibleWithIntegration(deviceId, appIntegrationId) {

        const integrationDetails = AppIntegrationsRepository.getById(appIntegrationId);
        const deviceDetails = this.getById(deviceId);

        /* check deviceType */
        const supportedDeviceTypes = Util.deepFind(integrationDetails, 'attributes.custom.manifest.devices') || [];
        if (supportedDeviceTypes.indexOf(deviceDetails.attributes.type) === -1) {

            return false;

        }

        /* check device OS */
        const supportedOS = Util.deepFind(integrationDetails, 'attributes.custom.manifest.os') || [];
        if (supportedOS.indexOf(deviceDetails.attributes.os) === -1) {

            return false;

        }

        return true;

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#isMobile
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function checks if a device is phone or tablet.
     *
     * @param {int} deviceId id of device
     *
     * @returns {boolean} True if the device is tablet or phone.
     */
    isMobile(deviceId) {

        const deviceDetails = this.getById(deviceId);

        if (deviceDetails) {

            return (deviceDetails.attributes.type === 'phone' || deviceDetails.attributes.type === 'tablet');

        }

        return false;

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#isVisible
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function checks if a device is visible.
     *
     * @param {Object} device device object
     *
     * @returns {boolean} True if the device is visible.
     */
    isVisible(device) {

        return device &&
            (device.attributes.state === 'online' ||
                device.attributes.state === 'offline') &&
            !device.attributes.hidden;

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#getAll
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function can be used to retrieves all devices from repository.
     *
     * @returns {Array} devices all devices from repository
     */
    getAll() {

        return _devices;

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#getById
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function can be used to retrieves a device based on specified id.
     *
     * @param {integer} deviceId id of device
     *
     * @returns {object} a device based on specified id
     */
    getById(deviceId) {

        return _devices.find(device => String(device.id) === String(deviceId));

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#getByStatus
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function can be used to retrieve devices with specified state.
     *
     * @param {Array} stateList array with device states.
     * @param {boolean} [includeHidden=true]  if true return also hidden devices
     *
     * @returns {Array} an array with devices filtered by state.
     */
    getByState(stateList, includeHidden = true) {

        return _devices.filter((device) => {

            if (stateList.indexOf(device.attributes.state) > -1) {

                if (!includeHidden && device.attributes.hidden) {

                    return false;

                }

                return device;

            }

            return false;

        });

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#getObservable
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function can be used to get an observable object for devices repository.
     * @returns {Object} an observable object for devices repository.
     */
    getObservable() {

        return observableDevices;

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#getDevicesByState
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function returns all devices by state online/offline
     *
     * @param {String} state online/offline
     *
     * @returns {Array} returns devices list.
     */
    getDevicesByState(state) {

        return _devices.filter(device =>
            device.attributes.state === state &&
            device.attributes.hidden === false);

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#getVisibleDevices
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function returns all visible devices.
     *
     * @returns {Array} returns visible devices list.
     */
    getVisibleDevices() {

        return _devices.filter(device =>
            device.attributes.hidden === false &&
            device.attributes.state !== 'pending');

    },

    /**
     * @ngdoc function
     * @name repositories.DevicesRepository#getDevicesByOs
     * @methodOf repositories.DevicesRepository
     * @function
     *
     * @description
     * This function returns all devices for a specifc OS.
     *
     * @param {String} os device OS.
     *
     * @returns {Array} returns filtered devices.
     */
    getDevicesByOs(os) {

        return _devices.filter(device =>
            device.attributes.os === os &&
            device.attributes.hidden === false &&
            device.attributes.state !== 'pending');

    },

};


export default DevicesRepository;



// WEBPACK FOOTER //
// ./scripts/common/repositories/-RFC.devicesRepository.js